import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setLocationEnabled, updateProfile } from '../store/authSlice';
import { closeSettings, showToast } from '../store/uiSlice';
import { logOut } from '../services/authService';
import { removeUserCoordinate } from '../services/locationService';
import { getEventCoords } from '../services/lastKnownPosition'; // CLAUDE-SYNC: v1.7.20
import { geoWatchRef } from '../App';

const ROLE_LABELS = {
  phleb: 'Phlebotomist',
  driver: 'Courier',
  admin: 'Admin / Supervisor',
  superadmin: "superadmin",
  phleb_lead: 'Phlebotomist Lead'
};

function SettingsPanel() {
  const dispatch = useDispatch();
  const { settingsOpen } = useSelector((state) => state.ui);
  const { user, isLocationEnabled } = useSelector((state) => state.auth);
  // CLAUDE-SYNC: v1.7 — guard against duplicate Sign Out taps on poor
  // connectivity, matching the TopBar duty-toggle fix (2026-08-06)
  const [isSigningOut, setIsSigningOut] = useState(false);

  const userInitials = user.name ? user?.name?.split(" ").map((l) => l[0]).join("").toUpperCase() : [user?.role?.split("")[0].toUpperCase(), user?.role?.split("")[5].toUpperCase()];

  const handleSave = () => {
    dispatch(showToast('Profile updated'));
  };

  if (!user) return null;

  // CLAUDE-SYNC: v1.7.20 — shared last-known-position helper (see services/lastKnownPosition.js)
  const getCoordsSafe = () => getEventCoords();

  const handleLogout = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      // CLAUDE-SYNC: v1.7 — confirm the duty-off call before touching local
      // state, same pattern as TopBar.jsx (2026-08-06)
      const coords = await getCoordsSafe();
      await removeUserCoordinate(false, coords);
      if (geoWatchRef.id !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.id);
        geoWatchRef.id = null;
      }
      dispatch(setLocationEnabled(false));

      const res = await logOut();
      if (res.status === 200) {
        dispatch(logout());
      }
    } catch (error) {
      console.log(error);
      dispatch(showToast('Could not sign out — check your connection and try again'));
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className={`settings-panel ${settingsOpen ? 'open' : ''}`}>
      <div className="detail-header">
        <button className="back-btn" onClick={() => dispatch(closeSettings())}>← Back</button>
        <div>
          <div className="detail-title">My Profile</div>
          <div className="detail-subtitle">Account settings &amp; preferences</div>
        </div>
      </div>

      <div className="detail-body">
        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0 20px' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', background: 'var(--berry)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, color: 'white', fontWeight: 600, fontFamily: "'Lora', serif",
          }}>
            {Array.isArray(userInitials) ? `${userInitials[0] ?? ""}${userInitials[1] ?? ""}` : userInitials}
          </div>
          <div style={{ fontFamily: "'Lora', serif", fontSize: 18, color: 'var(--rp)', marginTop: 10 }}>{user.name}</div>
          <div style={{ fontSize: 12, color: 'var(--bodygray)', marginTop: 2 }}>
            {ROLE_LABELS[user.role]} · CIMPAR Lab
          </div>
          <div style={{ marginTop: 10 }}>
            <span className="stop-badge s3">Active</span>
          </div>
        </div>

        {/* Contact info */}
        <div className="card">
          <div className="card-label">Contact Information</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--bodygray)', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Email</div>
              <input
                className="note-input"
                style={{ height: 'auto', padding: '10px 14px' }}
                defaultValue={user.email}
                id="settingEmail"
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--bodygray)', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Phone</div>
              <input
                className="note-input"
                style={{ height: 'auto', padding: '10px 14px' }}
                defaultValue={user.phone}
                type="tel"
                id="settingPhone"
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--bodygray)', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px' }}>SMS / Text number</div>
              {/* TWILIO: This number receives pickup-ready alerts */}
              <input
                className="note-input"
                style={{ height: 'auto', padding: '10px 14px' }}
                defaultValue={user.smsNumber}
                type="tel"
                id="settingSms"
              />
              <div style={{ fontSize: 11, color: 'var(--bodygray)', marginTop: 4 }}>Used for pickup-ready notifications</div>
            </div>
          </div>
          <button className="action-btn primary" style={{ marginTop: 16 }} onClick={handleSave}>
            Save Contact Info
          </button>
        </div>

        {/* Notifications */}
        <div className="card">
          <div className="card-label">Notifications</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {['Push notifications', 'SMS alerts'].map((label, i) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                paddingTop: i > 0 ? 10 : 0,
                borderTop: i > 0 ? '1px solid var(--rgray)' : 'none',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--bodygray)' }}>
                    {i === 0 ? 'Route updates, exception alerts' : 'Text message for urgent updates'}
                  </div>
                </div>
                {/* ROADMAP: Push/SMS notification toggles - Coming Soon */}
                <div style={{ background: '#F5F5F5', border: '1px dashed #ccc', color: 'var(--bodygray)', borderRadius: 20, fontSize: 10, fontWeight: 600, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                  Coming Soon
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <button className="action-btn danger" onClick={handleLogout} disabled={isSigningOut}>
            {isSigningOut ? 'Signing Out…' : 'Sign Out'}
          </button>
        </div>

        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <div style={{ fontSize: 10, color: '#bbb' }}>ReyaGo v3.0 · Employee ID: EMP-0042</div>
          <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>CIMPAR Lab Services · Chicago, IL</div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
